import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = `${API}/api/admin/commerciale`;
const CHECKLIST_ITEMS = [
  ["presentazione", "Presentazione effettuata"],
  ["esigenze", "Esigenze raccolte"],
  ["prossimi_passi", "Prossimi passi concordati"],
];
const EMPTY_DEMO = { prospect_id: "", data_demo: "", commerciale: "", commerciale_id: "", stato: "Programmata", note: "" };

function apiError(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

function getChecklist(value) {
  if (Array.isArray(value)) return value.reduce((result, item) => ({ ...result, [item]: true }), {});
  return value && typeof value === "object" ? value : {};
}

function localDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export default function DemoAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [demos, setDemos] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_DEMO);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [demosResponse, prospectsResponse, operatorsResponse] = await Promise.all([
        fetch(`${BASE_URL}/demo`, { headers }),
        fetch(`${BASE_URL}/prospects`, { headers }),
        fetch(`${BASE_URL}/operatori`, { headers }),
      ]);
      const [demosJson, prospectsJson, operatorsJson] = await Promise.all([
        demosResponse.json().catch(() => ({})),
        prospectsResponse.json().catch(() => ({})),
        operatorsResponse.json().catch(() => ({})),
      ]);
      if (!demosResponse.ok) throw new Error(apiError(demosJson, "Impossibile caricare le demo"));
      if (!prospectsResponse.ok) throw new Error(apiError(prospectsJson, "Impossibile caricare i prospect"));
      if (!operatorsResponse.ok) throw new Error(apiError(operatorsJson, "Impossibile caricare gli operatori"));
      setDemos(demosJson.data || []);
      setProspects(prospectsJson.data || []);
      setOperatori(operatorsJson.data || []);
    } catch (error) {
      toast.error(error.message || "Errore nel caricamento delle demo");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_DEMO);
    setOpen(true);
  };
  const openEdit = (demo) => {
    setEditingId(demo.id);
    setForm({
      prospect_id: String(demo.prospect_id || demo.prospect?.id || ""),
      data_demo: localDate(demo.data_demo),
      commerciale: demo.commerciale || "",
      commerciale_id: demo.commerciale_id || "",
      stato: demo.stato || "Programmata",
      note: demo.note || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.prospect_id || !form.data_demo) {
      toast.error("Seleziona il prospect e la data della demo.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(editingId ? `${BASE_URL}/demo/${editingId}` : `${BASE_URL}/demo`, {
        method: editingId ? "PUT" : "POST", headers, body: JSON.stringify({ ...form, prospect_id: Number(form.prospect_id) || form.prospect_id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiError(json, "Salvataggio demo non riuscito"));
      toast.success(editingId ? "Demo aggiornata" : "Demo programmata");
      setOpen(false);
      load();
    } catch (error) {
      toast.error(error.message || "Errore durante il salvataggio della demo");
    } finally {
      setSaving(false);
    }
  };

  const updateChecklist = async (demo, key, checked) => {
    const checklist = { ...getChecklist(demo.checklist), [key]: checked };
    try {
      const res = await fetch(`${BASE_URL}/demo/${demo.id}/checklist`, { method: "PATCH", headers, body: JSON.stringify({ checklist }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiError(json, "Aggiornamento checklist non riuscito"));
      setDemos((current) => current.map((item) => item.id === demo.id ? { ...item, checklist } : item));
      toast.success("Checklist aggiornata");
    } catch (error) {
      toast.error(error.message || "Errore durante l'aggiornamento della checklist");
    }
  };

  const remove = async (demo) => {
    if (!window.confirm("Eliminare questa demo?")) return;
    try {
      const res = await fetch(`${BASE_URL}/demo/${demo.id}`, { method: "DELETE", headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiError(json, "Eliminazione demo non riuscita"));
      setDemos((current) => current.filter((item) => item.id !== demo.id));
      toast.success("Demo eliminata");
    } catch (error) {
      toast.error(error.message || "Errore durante l'eliminazione");
    }
  };

  const prospectName = (demo) => demo.prospect?.nome_struttura || demo.nome_struttura || prospects.find((item) => String(item.id) === String(demo.prospect_id))?.nome_struttura || "Prospect non disponibile";

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={CalendarDays}
        title="Demo commerciali"
        description="Programmazione, preparazione e chiusura delle demo."
        actions={<><Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button><Button size="sm" onClick={openCreate}><Plus className="mr-1 h-4 w-4" /> Programma demo</Button></>}
      />
      {loading ? <div className="py-16 text-center text-slate-500"><Loader2 className="mr-2 inline h-5 w-5 animate-spin" />Caricamento demo…</div>
        : !demos.length ? <Card><CardContent className="p-10 text-center text-slate-500">Nessuna demo programmata.</CardContent></Card>
          : <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{demos.map((demo) => {
            const checklist = getChecklist(demo.checklist);
            const checkedCount = CHECKLIST_ITEMS.filter(([key]) => checklist[key]).length;
            return <Card key={demo.id}><CardContent className="space-y-3 p-4">
              <div className="flex items-start justify-between gap-2"><div><p className="font-semibold">{prospectName(demo)}</p><p className="text-sm text-slate-500">{demo.data_demo ? new Date(demo.data_demo).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "Data da definire"}</p></div><Badge variant={demo.stato === "completata" || demo.stato === "Completata" ? "default" : "secondary"}>{demo.stato || "Programmata"}</Badge></div>
              <p className="text-sm text-slate-600">Commerciale: {demo.commerciale || "Non assegnato"}</p>
              {demo.note && <p className="rounded bg-slate-50 p-2 text-sm text-slate-600">{demo.note}</p>}
              <div className="space-y-2 border-t pt-3"><p className="text-xs font-medium text-slate-500">CHECKLIST ({checkedCount}/{CHECKLIST_ITEMS.length})</p>{CHECKLIST_ITEMS.map(([key, label]) => <label key={key} className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={Boolean(checklist[key])} onCheckedChange={(checked) => updateChecklist(demo, key, Boolean(checked))} />{label}</label>)}</div>
              <div className="flex justify-end gap-1 border-t pt-2"><Button size="sm" variant="ghost" onClick={() => openEdit(demo)}><Pencil className="mr-1 h-4 w-4" /> Modifica</Button><Button size="icon" variant="ghost" onClick={() => remove(demo)} title="Elimina demo"><Trash2 className="h-4 w-4 text-rose-600" /></Button></div>
            </CardContent></Card>;
          })}</div>}

      <Dialog open={open} onOpenChange={setOpen}><DialogContent><DialogHeader><DialogTitle>{editingId ? "Modifica demo" : "Programma una demo"}</DialogTitle></DialogHeader>
        <div className="space-y-3"><div><Label>Prospect *</Label><Select value={form.prospect_id} onValueChange={(value) => setForm((current) => ({ ...current, prospect_id: value }))}><SelectTrigger><SelectValue placeholder="Seleziona struttura" /></SelectTrigger><SelectContent>{prospects.map((prospect) => <SelectItem key={prospect.id} value={String(prospect.id)}>{prospect.nome_struttura}</SelectItem>)}</SelectContent></Select></div><div><Label>Data e ora *</Label><Input type="datetime-local" value={form.data_demo} onChange={(event) => setForm((current) => ({ ...current, data_demo: event.target.value }))} /></div><div><Label>Stato</Label><Select value={form.stato} onValueChange={(value) => setForm((current) => ({ ...current, stato: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Programmata", "Effettuata", "No-show", "Annullata"].map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}</SelectContent></Select></div><div><Label>Commerciale</Label><Select value={form.commerciale || "__none__"} onValueChange={(value) => { const operator = operatori.find((item) => item.email === value); setForm((current) => ({ ...current, commerciale: value === "__none__" ? "" : value, commerciale_id: operator?.id || "" })); }}><SelectTrigger><SelectValue placeholder="Non assegnato" /></SelectTrigger><SelectContent><SelectItem value="__none__">Non assegnato</SelectItem>{operatori.map((operator) => <SelectItem key={operator.id || operator.email} value={operator.email}>{operator.nome || operator.name || operator.email}</SelectItem>)}</SelectContent></Select></div><div><Label>Note</Label><Textarea rows={3} value={form.note} onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))} /></div></div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
